import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import type { Database } from "@/integrations/supabase/types";

export type Thread        = Database["public"]["Tables"]["threads"]["Row"];
export type ThreadMessage = Database["public"]["Tables"]["thread_messages"]["Row"];

// ── Fetch threads (for current user or by application) ────────────────────────
export function useThreads(applicationId?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["threads", applicationId, user?.id],
    refetchInterval: 30_000,
    queryFn: async () => {
      let q = supabase.from("threads").select("*").order("updated_at", { ascending: false });
      if (applicationId) q = q.eq("application_id", applicationId);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

// ── Fetch messages for a thread ────────────────────────────────────────────────
export function useThreadMessages(threadId: string | undefined) {
  return useQuery({
    queryKey: ["thread_messages", threadId],
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("thread_messages")
        .select("*")
        .eq("thread_id", threadId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!threadId,
  });
}

// ── Create a new thread with first message ────────────────────────────────────
export function useCreateThread() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      subject: string;
      body: string;
      applicationId?: string;
      leadId?: string;
      category?: Thread["category"];
      channel?: Thread["channel"];
      source?: ThreadMessage["source"];
    }) => {
      const { data: thread, error } = await supabase
        .from("threads")
        .insert({
          user_id: user?.id ?? null,
          lead_id: input.leadId ?? null,
          subject: input.subject,
          application_id: input.applicationId ?? null,
          category: input.category ?? "general",
          channel: input.channel ?? "portal",
        })
        .select()
        .single();
      if (error) throw error;

      // Create first message
      const { error: messageError } = await supabase.from("thread_messages").insert({
        thread_id: thread.id,
        sender_id: user!.id,
        body: input.body,
        is_internal: false,
        direction: "inbound",
        sender_type: "user",
        channel: input.channel ?? null,
        source: input.source ?? "portal",
      });

      if (messageError) throw messageError;

      return thread;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["threads"] }),
  });
}

// ── Send a message in an existing thread ──────────────────────────────────────
export function useSendMessage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      threadId: string;
      body: string;
      isInternal?: boolean;
      attachments?: Array<{ name: string; path: string; size: number; type: string }>;
      direction?: ThreadMessage["direction"];
      senderType?: ThreadMessage["sender_type"];
      channel?: ThreadMessage["channel"];
      source?: ThreadMessage["source"];
      parentMessageId?: string;
    }) => {
      const { data, error } = await supabase
        .from("thread_messages")
        .insert({
          thread_id: input.threadId,
          sender_id: user!.id,
          body: input.body,
          is_internal: input.isInternal ?? false,
          attachments: (input.attachments ?? []) as any,
          direction: input.direction ?? "inbound",
          sender_type: input.senderType ?? "user",
          channel: input.channel ?? null,
          source: input.source ?? "portal",
          parent_message_id: input.parentMessageId ?? null,
        })
        .select()
        .single();
      if (error) throw error;

      // Touch thread updated_at
      await supabase
        .from("threads")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", input.threadId);

      // Fire notification (non-blocking)
      if (!input.isInternal) {
        supabase.functions.invoke("notify-message", {
          body: {
            threadId: input.threadId,
            messageId: data.id,
            messageBody: input.body,
            senderId: user!.id,
          },
        }).catch(console.error);
      }

      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["thread_messages", vars.threadId] });
      qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}
